import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { registerAppSumo, login } from '../api/auth';
import aerochatLogo from '../assets/aerochat_logo_full.svg';
import '../styles/pages/login.css';

const AppSumoRegisterPage = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('appsumo');
  const [searchParams] = useSearchParams();
  const licenseKey = (searchParams.get('license_key') || '').trim();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);

  // Block the page from being opened without a license_key — that
  // would only happen if a user typed the URL directly. Redirect
  // them to standard login.
  useEffect(() => {
    if (!licenseKey) {
      navigate('/login', { replace: true });
    }
  }, [licenseKey, navigate]);

  const maskedKey = licenseKey.length > 12
    ? `${licenseKey.slice(0, 8)}-***-${licenseKey.slice(-4)}`
    : '***';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError(t('validation.fillRequired'));
      return;
    }
    if (password.length < 6) {
      setError(t('validation.passwordLength'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await registerAppSumo({
        licenseKey,
        email: email.trim(),
        password,
      });

      // Auto-login after successful redemption
      const data = await login(email.trim(), password);
      onLoginSuccess(data.user);
      navigate('/platform-select', { replace: true });
    } catch (err) {
      // Existing account (backend sets reason=account_exists). Registration is
      // for NEW accounts only — don't auto-handle it here. Show a clear message
      // and surface the explicit "log in to attach" call-to-action so the user
      // authenticates deliberately.
      if (err?.data?.reason === 'account_exists') {
        setExistingAccount(true);
        setError(t('attach.accountExists'));
        return;
      }
      // Surface the backend's friendly error message verbatim.
      setError(err.message || t('validation.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!licenseKey) return null;

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__logo">
          <img src={aerochatLogo} alt="AeroChat" className="login-card__logo-img" />
        </div>

        <div className="login-card__header">
          <h1 className="login-card__title">{t('title')}</h1>
          <p className="login-card__subtitle">{t('subtitle')}</p>
        </div>

        <div className="login-card__plan-banner">
          <span className="login-card__plan-badge">{t('licenseBadge')}</span>
          <span className="login-card__plan-trial-text">
            {t('licenseRow', { key: maskedKey })}
          </span>
        </div>

        <form className="login-card__form" onSubmit={handleSubmit}>
          {error && <div className="login-card__error">{error}</div>}

          <div className="login-card__field">
            <label className="login-card__label">{t('emailLabel')}</label>
            <input
              type="email"
              className="login-card__input"
              placeholder={t('emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>

          <div className="login-card__field">
            <label className="login-card__label">{t('passwordLabel')}</label>
            <div className="login-card__input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                className="login-card__input login-card__input--password"
                placeholder={t('passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="login-card__eye-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="login-card__submit login-card__submit--green"
            disabled={loading || existingAccount}
          >
            {loading ? t('redeeming') : t('redeem')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AppSumoRegisterPage;
